import type {ReactNode, ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {motion, AnimatePresence} from 'framer-motion';
import styles from './option.module.scss';
import {Suboption} from '../suboption/suboption';

interface OptionProps extends OptionsProperties {
  className?: string;
  containerClassName?: string;
  label?: string;
  labelClassName?: string;
  isLabelBold?: boolean;
  optionControl: ReactElement<any>;
  showControlInCenter?: boolean;
  children?: ReactNode;
  subOptionClassName?: string;
  isDisabled?: boolean;
  isForSubOption?: boolean;

  subOptionHasOverflow?: boolean;
  tag?: ReactNode;
  doesSuboptionHaveTransition?: boolean;
}

interface OptionsProperties {
  optionItemsHaveGap?: boolean;
}

export function Option({
  showControlInCenter = false,
  label = '',
  labelClassName = '',
  isLabelBold = false,
  className = '',
  children = null,
  tag = null,
  subOptionClassName = '',
  optionItemsHaveGap = true,
  isDisabled = false,
  isForSubOption = false,
  containerClassName = '',
  subOptionHasOverflow = false,
  doesSuboptionHaveTransition = true,
  ...props
}: OptionProps): ReactElement<any> {
  const getTextColorClasses = (): string => {
    if (isDisabled) {
      if (isForSubOption) {
        return 'content-disabled';
      }
      return 'color-extra-light';
    }
    return 'content-sub-text';
  };

  const getOption = (): ReactElement<any> => {
    return (
      <div
        className={`flex-items-center ${styles.container} ${optionItemsHaveGap ? styles.gap : ''} ${
          showControlInCenter && label === '' ? ' flex-row-justify-center' : ' flex-row-justify-between'
        } ${className}`}
      >
        {tag ? (
          <div className="flex-h-row flex-items-center">
            {label ? <Text val={label} bold={isLabelBold} size={TextSize.XSMALL} className={`${getTextColorClasses()} ${labelClassName} spacing-m-r-2`} /> : null}
            {tag}
          </div>
        ) : (
          <>{label ? <Text val={label} bold={isLabelBold} size={TextSize.XSMALL} className={`${getTextColorClasses()} ${labelClassName}`} /> : null}</>
        )}
        {props.optionControl}
      </div>
    );
  };

  const getSubOption = (): ReactElement<any> | null => {
    if (doesSuboptionHaveTransition) {
      return (
        <AnimatePresence initial={false}>
          {children ? (
            <motion.div
              className={subOptionHasOverflow ? '' : styles.animationContainer}
              layout
              initial={{height: 0}}
              animate={{height: 'auto'}}
              transition={{duration: 0.2}}
              exit={{height: 0}}
            >
              <Suboption className={subOptionClassName}>{children}</Suboption>
            </motion.div>
          ) : null}
        </AnimatePresence>
      );
    }

    if (!children) {
      return null;
    }

    return <Suboption className={subOptionClassName}>{children}</Suboption>;
  };

  const getOptionsContainer = (): ReactElement<any> => {
    return (
      <li className={`${styles.optionsContainer} ${containerClassName}`}>
        {getOption()}
        {getSubOption()}
      </li>
    );
  };

  return getOptionsContainer();
}
